/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import EnvironmentModel from 'cio/models/environment';
import { IdentifierKey } from 'cio/utils/identifiers';
import { inject as service } from '@ember/service';
import { announceHydraNavigate } from '@customerio/hydra-nav-state';
import HydraService from 'cio/services/hydra';
import RouterService from '@ember/routing/router-service';
import AccountService from 'cio/services/account';
import AccountUsageModel from 'cio/models/account-usage';
import { compactNumber } from 'cio/utils/formatters';
import moment from 'moment-timezone';

interface TableRowSignature {
  Args: {
    environment: EnvironmentModel;
    disableEdit: boolean;
    disableDelete: boolean;
    tooltipDelete: string;
    environmentsUsage: AccountUsageModel['environmentUsages'];
  };
  Element: HTMLTableRowElement;
}

export default class TableRow extends Component<TableRowSignature> {
  @service hydra!: HydraService;
  @service router!: RouterService;
  @service account!: AccountService;

  get colorBarStyleString() {
    let { hexCode } = this.args.environment;
    return hexCode ? `background-color: ${hexCode};` : undefined;
  }

  // computed here since ember seems to have a hard time tracking this deeply nested object, at
  // least in tests.
  // eslint-disable-next-line getter-return
  @computed('args.environment.enabledIdentifiers.[]')
  get identifierType() {
    let { environment } = this.args;

    if (environment.isEmailIdentified) {
      return IdentifierKey.Email;
    }

    return environment.enabledIdentifiers.join(', ') ?? '';
  }

  get usage() {
    let { environment, environmentsUsage } = this.args;
    let data = environmentsUsage.find((usage) => usage.environmentId.toString() === environment.id);

    if (data == null) {
      return [];
    }

    return [
      {
        label: `Current People count: ${compactNumber(data.customersTotal, { notation: 'standard' })}`,
        compact: compactNumber(data.customersTotal, { maximumFractionDigits: 0 }),
        icon: 'person',
      },
      {
        label: `Current Objects count: ${compactNumber(data.objectsTotal, { notation: 'standard' })}`,
        compact: compactNumber(data.objectsTotal, { maximumFractionDigits: 0 }),
        icon: 'object',
      },
      {
        label: `Since ${moment(this.account.current.planPeriodStart * 1000).format(
          'MMM Do, YYYY',
        )} this workspace has sent roughly ${compactNumber(data.billableMessagesSent, {
          notation: 'standard',
        })} billable messages. This is an approximation and not used directly for billing or your invoice.`,
        compact: compactNumber(data.billableMessagesSent, { maximumFractionDigits: 0 }),
        icon: 'email',
      },
    ];
  }

  @action
  onLinkClick(event: MouseEvent) {
    event.preventDefault();
    if ((event.currentTarget as HTMLAnchorElement).classList.contains('is-disabled')) {
      return;
    }

    let targetUrl = (event.currentTarget as HTMLAnchorElement).href.replace(window.location.origin, '');

    if (!this.hydra.isHydraEnabled) {
      this.router.transitionTo(targetUrl);
      return;
    }

    announceHydraNavigate(targetUrl);
  }
}
